import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Home, Marketplace, PropertyDetail, CalculatorPage, StaticPage } from "@/pages/Public";
import { Login, Register, AuthCallback } from "@/pages/Auth";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import { SecondaryMarket } from "@/pages/Secondary";
import { Portfolio, KycPage, Statements, Referrals, Withdrawals, PaymentSuccess, PaymentCancel } from "@/pages/Investor";
import { AdminDashboard, AdminProperties, AdminInvestors, AdminKyc, AdminOrders, AdminDistributions, AdminWithdrawals, AdminAudit } from "@/pages/Admin";
import { AdminTeam } from "@/pages/AdminTeam";
import { PublicLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";

function NotFound() {
  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="overline">404</div>
        <h1 className="font-serif text-5xl mt-3 text-[#0B192C]">Page not found.</h1>
        <p className="mt-4 text-slate-600">The page you're looking for doesn't exist or has moved.</p>
        <Link to="/"><Button className="btn-gold rounded-full mt-8" data-testid="notfound-home-btn">Back to home</Button></Link>
      </div>
    </PublicLayout>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/secondary" element={<SecondaryMarket />} />
      <Route path="/property/:id" element={<PropertyDetail />} />
      <Route path="/calculator" element={<CalculatorPage />} />
      <Route path="/how-it-works" element={<StaticPage title="How OwnNest Works"><p>OwnNest transforms trophy real-estate into fractional, income-producing assets you can own from $1,000. Every property is held in a dedicated SPV with escrow-protected capital and title insurance. Investors receive assured monthly or quarterly rentals plus a share in long-term capital appreciation.</p></StaticPage>} />
      <Route path="/about" element={<StaticPage title="About OwnNest"><p>OwnNest is an institutional-grade fractional-ownership platform building the future of accessible luxury real-estate investing.</p></StaticPage>} />
      <Route path="/contact" element={<StaticPage title="Contact"><p>Email us at <a href="mailto:hello@ownnest.co" className="text-[#C5A059]">hello@ownnest.co</a>. Our investor-relations desk answers within one business day.</p></StaticPage>} />
      <Route path="/faq" element={<StaticPage title="FAQ"><p>See the FAQ section on our home page. For anything else, contact investor relations.</p></StaticPage>} />
      <Route path="/terms" element={<StaticPage title="Terms of Service"><p>By using OwnNest you agree to our terms. All investments are subject to a signed subscription agreement, SPV operating agreement, and risk disclosure.</p></StaticPage>} />
      <Route path="/privacy" element={<StaticPage title="Privacy"><p>We handle your data with SOC-2 aligned controls and never sell personal information.</p></StaticPage>} />
      <Route path="/risk" element={<StaticPage title="Risk Disclosure"><p>Real estate investments are illiquid and subject to market, tenant, occupancy, regulatory and macro risks. Past performance is not indicative of future results. Please read the full risk disclosure before investing.</p></StaticPage>} />

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
      <Route path="/kyc" element={<ProtectedRoute><KycPage /></ProtectedRoute>} />
      <Route path="/statements" element={<ProtectedRoute><Statements /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/withdrawals" element={<ProtectedRoute><Withdrawals /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      <Route path="/admin" element={<ProtectedRoute role="staff"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/properties" element={<ProtectedRoute role={["admin", "property_uploader"]}><AdminProperties /></ProtectedRoute>} />
      <Route path="/admin/investors" element={<ProtectedRoute role={["admin", "sales_team"]}><AdminInvestors /></ProtectedRoute>} />
      <Route path="/admin/kyc" element={<ProtectedRoute role={["admin", "sales_team"]}><AdminKyc /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute role={["admin", "accounts"]}><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/distributions" element={<ProtectedRoute role={["admin", "accounts"]}><AdminDistributions /></ProtectedRoute>} />
      <Route path="/admin/withdrawals" element={<ProtectedRoute role={["admin", "accounts"]}><AdminWithdrawals /></ProtectedRoute>} />
      <Route path="/admin/team" element={<ProtectedRoute role="admin"><AdminTeam /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute role="admin"><AdminAudit /></ProtectedRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <CurrencyProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </CurrencyProvider>
  );
}

export default App;
